<template>
  <VueModal modal-class="modal modal-lg"  title="Edit Profile: Intro Block" v-model="modalOpen">
    <form>
      <div class="mb-6">
        <label for="dropzone" ref="image">
          Profile Photo
          <span class="mb-5 text-sm font-sans-book block">
          Please upload a horizontal/landscape image (JPEG or PNG), ideally 400 pixels x 300 pixels. This should represent you or your organization. If you are an individual representing the entire organization, you may use a photo of your face. Be sure to click Update at the bottom of the page once you have uploaded your photo.
        </span>
        </label>

        <vue-dropzone
          ref="image"
          id="dropzone"
          :options="dropzoneOptions"
          @vdropzone-success="uploaded"
          @vdropzone-error="updateImageErrorMessage"
          @vdropzone-removed-file="resetImage"
          :useCustomSlot=true
        >
          <div class="after-drop-message text-n-gray-dark">
            <img :src="imgSrc" width="175" class="mb-3"  alt="Placeholder image"/>
            <p>You may drag-and-drop an image here. File size must be less than 4 MB.</p>
          </div>
        </vue-dropzone>

        <span v-if="imageError" class="text-lg error text-error-red">
          {{ imageError }}
        </span>
      </div>

      <div class="mb-12">
        <label for="name">
          <span v-if="organization.type === 'organization'">

          </span>
          {{ organization.type === 'individual' ? 'Public' : 'Organization' }} Name
        </label>

        <input id="name" type="text" name="name" v-model="form.name" />
      </div>

      <div class="mb-12">
        <label>
          What country {{ organization.type === 'individual' ? 'are you' : 'is your organization' }} located in?&nbsp;<span class="text-red">*</span>
        </label>
        <select v-model="form.country" v-validate="'required'" name="country">
          <option :value="null" disabled> -- please select -- </option>
          <option v-for="country in options.countries" :key="country.code" :value="country.code">
            {{ country.name }}
          </option>
        </select>
        <div v-show="errors.has('country')" class="error-message">
          {{ errors.first('country') }}
        </div>
      </div>

      <div class="flex flex-col md:flex-row mb-12">
        <div :class="[isFromUnitedStates ? 'w-full md:w-1/2' : 'w-full']">
          <label>
            City <span class="text-red">*</span>
          </label>
          <input v-validate="'required'" name="city" v-model="form.city" type="text" />
          <div v-show="errors.has('city')" class="error-message">
            {{ errors.first('city') }}
          </div>
        </div>

        <div class="spacer block md:hidden" />

        <template v-if="isFromUnitedStates">
          <div class="w-full md:w-1/4 pl-0 md:pl-2">
            <label>
              State <span class="text-red">*</span>
            </label>
            <select v-validate="'required'" name="state" v-model="form.state">
              <option :value="null">-- select --</option>
              <option v-for="state in options.states" :key="state.code">
                {{ state.name }}
              </option>
            </select>
            <div v-show="errors.has('state')" class="error-message">
              {{ errors.first('state') }}
            </div>
          </div>

          <div class="spacer block md:hidden" />

          <div class="w-full md:w-1/4 pl-0 md:pl-2">
            <label>
              Zip code <span class="text-red">*</span>
            </label>
            <input v-validate="'required'" name="zip_code" v-model="form.zip_code" type="text" />
            <div v-show="errors.has('zip_code')" class="error-message">
              {{ (errors.first('zip_code') || '').replace('_', ' ') }}
            </div>
          </div>
        </template>
      </div>

      <div class="mb-12">
        <label for="instagramUrl">
          Website
        </label>

        <url-input id="instagramUrl" v-model="form.website" />
      </div>

      <div class="mb-12">
        <label for="publicEmail">
          Email
        </label>
        <input id="publicEmail" type="text" name="publicEmail" v-model="form.email" />
      </div>

      <h2 class="font-sans text-n-gray-dark font-hairline mb-6">
        Social Profiles
      </h2>

      <div class="mb-12">
        <label for="facebookUrl">
          Facebook URL
        </label>

        <url-input id="facebookUrl" v-model="form.facebookUrl" />
      </div>

      <div class="mb-12">
        <label for="twitterUrl">
          Twitter URL
        </label>

        <url-input id="twitterUrl" v-model="form.twitterUrl" />
      </div>

      <div class="mb-12">
        <label for="instagramUrl">
          Instagram URL
        </label>

        <url-input id="instagramUrl" v-model="form.instagramUrl" />
      </div>

      <div class="mb-12">
        <label for="linkedinUrl">
          LinkedIn URL
        </label>

        <url-input id="linkedinUrl" v-model="form.linkedinUrl" />
      </div>

      <div class="flex justify-center">
        <button type="button" class="button" @click="update">
          Update
        </button>
      </div>

      <div class="mb-6" />

    </form>
  </VueModal>
</template>

<script>
import VueModal from '@kouts/vue-modal'
import UrlInput from "../../../../global/UrlInput.vue";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import {mapGetters} from "vuex";
import {dropzoneHelper} from "../../../../../helpers/dropzoneHelper";
import {geocodeAPI} from "../../../../../api/geocodeAPI";
import {organizationAPI} from "../../../../../api/organizationAPI";

export default {
  props: { value: { required: true, type: Boolean } },
  name: "OrganizationEditModal",
  components: { UrlInput, VueModal, vueDropzone: vue2Dropzone },
  computed: {
    ...mapGetters({ organization: "organization",  tokens: "tokens",  user: "user" }),
    isFromUnitedStates() { return this.form.country === 'USA'; },
    dropzoneOptions() { return dropzoneHelper.options(this.tokens.access_token); },
    imgSrc() {
      return this.organization && this.organization.profilePhoto ? this.organization.profilePhoto : '/img/profile/placeholder.png';
    },
    modalOpen: {
      get() {
        return this.value;
      },
      set(localModalIsSet) {
        this.$emit('input', localModalIsSet);
      }
    }

  },
  data() {
    return {
      options: {
        countries: geocodeAPI.getCountries(),
        states: geocodeAPI.getStates(),
      },
      form: {
        name: '',
        country: 'USA',
        city: '',
        state: null,
        zip_code: '',
        phone_number: '',
        website: '',
        email: '',
        facebookUrl: '',
        twitterUrl: '',
        instagramUrl: '',
        linkedinUrl: '',
        newImage: '',
      },
      imageError: '',
      loading: false
    }
  },
  watch: {
    organization() {
      this.setForm();
    }
  },
  methods: {
    async update() {
      if (this.loading) {
        return;
      }

      this.loading = true;

      await organizationAPI.updateBasicProfile(this.form);
      await this.$store.dispatch('getMe');
      await this.$store.dispatch('getOrganization', { id: this.organization.id });

      this.loading = false;
      this.modalOpen = false;
    },
    setForm() {
      this.form = {
        name: this.organization.name,
        country: this.organization.country_code,
        city: this.organization.city,
        state: this.organization.state,
        zip_code: this.organization.zip_code,
        phone_number: this.organization.publicPhone,
        website: this.organization.url,
        email: this.organization.publicEmail,
        facebookUrl: this.organization.facebookUrl,
        twitterUrl: this.organization.twitterUrl,
        instagramUrl: this.organization.instagramUrl,
        linkedinUrl: this.organization.linkedinUrl,
      };
    },
    resetImage() {
      this.imageError = "";

      axios.delete(laroute.route("api.temp-images.destroy"), {data: { filePath: this.form.newImage }})
        .then(() => {
          this.form.newImage = "";
        })
        .catch(error => {
          this.displayError(error);
        });
    },
    updateImageErrorMessage(file, message) {
      this.imageError = message;
    },
    uploaded(file, path) {
      this.form.newImage = path;
    }
  },
  mounted() {
    this.setForm();
  }
}
</script>

<style scoped>
.spacer {
  min-height: 3rem;
}
#dropzone {
  @apply bg-gray-light;
}
</style>